import React, { useEffect, useState } from 'react';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import { appColors } from '../../Config/Theme';
import type { HelpTopicSection } from '../../help/types';

interface HelpOnThisPageNavProps {
  sections: HelpTopicSection[];
}

const HelpOnThisPageNav: React.FC<HelpOnThisPageNavProps> = ({ sections }) => {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 },
    );

    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sections]);

  if (sections.length < 2) return null;

  return (
    <Box
      component="nav"
      aria-label="On this page"
      sx={{
        position: 'sticky',
        top: 80,
        pl: 3,
        borderLeft: `2px solid ${appColors.border.default}`,
        minWidth: 180,
        maxWidth: 220,
        display: { xs: 'none', lg: 'block' },
      }}
    >
      <Typography variant="overline" sx={{ fontWeight: 700, color: appColors.text.caption, fontSize: '0.65rem', mb: 1, display: 'block' }}>
        On this page
      </Typography>
      {sections.map((s) => (
        <MuiLink
          key={s.id}
          href={`#${s.id}`}
          underline="none"
          sx={{
            display: 'block',
            py: 0.5,
            fontSize: '0.78rem',
            fontWeight: activeId === s.id ? 600 : 400,
            color: activeId === s.id ? appColors.primary.main : appColors.text.body,
            transition: 'color 0.15s',
            '&:hover': { color: appColors.primary.main },
          }}
        >
          {s.title}
        </MuiLink>
      ))}
    </Box>
  );
};

export default HelpOnThisPageNav;
